'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { marketDataService } from '@/services/market-data.service';
import { LiveTickerItem } from '@/types/market-data.types';

interface LiveTickerProps {
  symbols?: string[];
}

const DEFAULT_SYMBOLS = ['BTC/USD', 'ETH/USD', 'EUR/USD', 'GBP/USD', 'XRP/USD', 'ADA/USD'];

const LiveTicker = ({ symbols = DEFAULT_SYMBOLS }: LiveTickerProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [tickerItems, setTickerItems] = useState<LiveTickerItem[]>([]);
  const [previousPrices, setPreviousPrices] = useState<Map<string, number>>(new Map());
  const [priceAnimations, setPriceAnimations] = useState<Map<string, 'up' | 'down' | null>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const fetchLiveData = async () => {
      try {
        if (isLoading) {
          setError(null);
        }
        const data = await marketDataService.getLiveTickerData(symbols);
        
        // Track price changes for animations
        const newAnimations = new Map<string, 'up' | 'down' | null>();
        data.forEach(item => {
          const prevPrice = previousPrices.get(item.pair);
          if (prevPrice !== undefined && prevPrice !== item.price) {
            newAnimations.set(item.pair, item.price > prevPrice ? 'up' : 'down');
          }
        });

        setPriceAnimations(newAnimations);
        
        // Update previous prices
        const newPreviousPrices = new Map<string, number>();
        data.forEach(item => {
          newPreviousPrices.set(item.pair, item.price);
        });
        setPreviousPrices(newPreviousPrices);

        setTickerItems(data);

        // Clear animations after they complete
        setTimeout(() => {
          setPriceAnimations(new Map());
        }, 800);

      } catch (err) {
        console.error('Error fetching live ticker data:', err);
        setError('Unable to load live market data. Please check your TwelveData API configuration.');
        setTickerItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLiveData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchLiveData, 30000);

    return () => clearInterval(interval);
  }, [isHydrated, symbols]);

  if (!isHydrated || isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 overflow-hidden">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (error || tickerItems.length === 0) {
    return (
      <div className="bg-card border border-error/50 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-error"></div>
          <span className="text-sm font-semibold text-error">Market Data Unavailable</span>
        </div>
        <p className="text-sm text-muted-foreground">
          {error || 'Failed to connect to TwelveData API. Please verify your API key configuration.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 overflow-hidden">
      <div className="flex items-center space-x-2 mb-3">
        <div className={`w-2 h-2 rounded-full ${error ? 'bg-error' : 'bg-success animate-dot-pulse'}`}></div>
        <span className="text-sm font-semibold text-foreground">
          {error ? 'Market Data (Cached)' : 'Live Market Data'}
        </span>
        {!error && (
          <span className="text-xs text-muted-foreground">
            • Updates every 30s
          </span>
        )}
      </div>
      <div className="relative overflow-hidden">
        <div className="flex animate-scroll space-x-6">
          {[...tickerItems, ...tickerItems].map((item, index) => {
            const showMarketClosed = !item.isMarketOpen && !item.isCrypto;
            const priceAnimation = priceAnimations.get(item.pair);
            
            return (
              <div 
                key={index} 
                className={`flex items-center space-x-3 whitespace-nowrap transition-all duration-300 ${
                  priceAnimation ? `animate-flash-${priceAnimation === 'up' ? 'green' : 'red'}` : ''
                }`}
              >
                <span className="text-sm font-semibold text-foreground">{item.pair}</span>
                {item.isCrypto && (
                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-primary/20 text-primary">
                    24/7
                  </span>
                )}
                {showMarketClosed ? (
                  <span className="text-xs text-warning font-medium">Market Closed</span>
                ) : (
                  <>
                    <span className={`text-sm text-muted-foreground price-transition ${priceAnimation ? `animate-price-${priceAnimation}` : ''}`}>
                      ${item.price.toFixed(2)}
                      {priceAnimation && (
                        <span className={`ml-1 inline-block ${priceAnimation === 'up' ? 'text-success' : 'text-error'}`}>
                          {priceAnimation === 'up' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                    <div className={`flex items-center space-x-1 text-sm font-semibold transition-colors duration-300 ${item.isPositive ? 'text-success' : 'text-error'}`}>
                      <Icon 
                        name={item.isPositive ? 'ArrowUpIcon' : 'ArrowDownIcon'} 
                        size={12}
                        className="transition-transform duration-300"
                      />
                      <span>{item.isPositive ? '+' : ''}{item.changePercent.toFixed(2)}%</span>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LiveTicker;